import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { servicesAPI } from '../../services/api';

// Get initial state
const getInitialState = () => {
  return {
    services: [],
    summary: null,
    isLoading: false,
    error: null,
  };
};

// ==================== GET SERVICES ====================

export const getServices = createAsyncThunk(
  'services/getServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await servicesAPI.getServices();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch services.'
      );
    }
  }
);

// ==================== TOGGLE SERVICES ====================

export const toggleServices = createAsyncThunk(
  'services/toggleServices',
  async (data, { rejectWithValue }) => {
    try {
      const response = await servicesAPI.toggleServices(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to toggle services.'
      );
    }
  }
);

// ==================== SERVICES SLICE ====================

const servicesSlice = createSlice({
  name: 'services',
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Optimistic update for main service toggle
    toggleMainServiceOptimistic: (state, action) => {
      const { serviceId, enabled } = action.payload;
      const service = state.services.find(s => s.ServiceId === serviceId);
      if (service) {
        service.IsMainServiceActive = enabled;
      }
    },
    // Optimistic update for sub-service toggle
    toggleSubServiceOptimistic: (state, action) => {
      const { serviceId, subServiceId, enabled } = action.payload;
      const service = state.services.find(s => s.ServiceId === serviceId);
      if (service && service.SubServices) {
        const subService = service.SubServices.find(ss => ss.SubServiceId === subServiceId);
        if (subService) {
          subService.IsActive = enabled;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // GET SERVICES
    builder
      .addCase(getServices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getServices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.services = action.payload.Services || action.payload || [];
        state.summary = action.payload.Summary || null;
        state.error = null;
      })
      .addCase(getServices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // TOGGLE SERVICES
    builder
      .addCase(toggleServices.pending, (state) => {
        // Don't set isLoading to avoid full page refresh
        state.error = null;
      })
      .addCase(toggleServices.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(toggleServices.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, toggleMainServiceOptimistic, toggleSubServiceOptimistic } = servicesSlice.actions;
export default servicesSlice.reducer;
